import { isAxiosNetworkError } from "./error-classification";

// MN-8803/MN-8956: gateway 5xx by status, on both fetch (error.status) and axios
// (error.response.status) error shapes.
export const isTransientStatus = error =>
  [502, 503, 504].includes(error?.status ?? error?.response?.status);

// A caller-supplied AbortController cancellation (fetch AbortError / axios
// CanceledError) must NEVER be retried — the caller wants out. This is guarded
// first because haltErrorResponse stamps a gateway status onto a body-read that
// aborts mid-5xx; without this guard that stamped status would make an abort look
// transient and re-enter fetch with an already-aborted signal (DTF polling and
// other GETs pass timeout/cancellation signals).
const isAbortError = error =>
  error?.name === "AbortError" ||
  error?.name === "CanceledError" ||
  error?.code === "ABORT_ERR" ||
  error?.code === "ERR_CANCELED";

// MN-8956: the single idempotent-GET retry predicate shared by BOTH transports
// (fetch + axios) — retry a transient gateway status (502/503/504) OR a network
// failure. The network branch is gated on there being NO HTTP status, mirroring
// shouldReportError's guard: a real server error (e.g. a 500 whose body contains
// "offline") must not be retried just because its message matches a network
// pattern. One predicate keeps the two transports from drifting apart (fetch
// previously matched status-only; axios matched network-only).
export const isRetriableGetError = error => {
  if (isAbortError(error)) return false;
  if (isTransientStatus(error)) return true;
  const httpStatus = error?.status ?? error?.response?.status;
  return !httpStatus && isAxiosNetworkError(error);
};

// MN-8956: a retrying GET collapses the inner fetchWithRetry to a single attempt
// so the one outer retry owns the whole attempt budget — otherwise the inner
// network-retry (up to 3) multiplies with the outer retry into a ~6–9 fetch
// fan-out for one GET.
export const SINGLE_FETCH_ATTEMPT = { maxAttempts: 1, retryDelay: 0 };
